import type { FormArray, FormGroup, UntypedFormControl } from '@angular/forms';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ControlContainer, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { FieldMap } from 'angular2-query-builder/dist/components';
import { AdditionalField } from 'app/store/project/addition-fields/addition-fields.reducer';
import { additionalFieldsWithNumbers } from 'app/store/project/addition-fields/addition-fields.selectors';
import { GoodOutput, SenderOutput, StatusOutput, StorehouseOutput } from 'core/http/project';
import { Observable } from 'rxjs';
import { getFormGroup } from 'shared/modules/addition-fields/addition-add-dialog/get-form-group';
import { DynamicField } from 'shared/modules/dynamic-form/field-types';

@Component({
  selector: 'app-addition-form',
  templateUrl: './addition-form.component.html',
  styleUrls: ['./addition-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdditionFormComponent {
  private _goods = new Array<GoodOutput>();
  private _fields = new Array<DynamicField>();
  private _senders = new Array<SenderOutput>();
  private _statuses = new Array<StatusOutput>();
  private _storehouses = new Array<StorehouseOutput>();
  private _fieldMap: FieldMap = {};
  private _additionalFields$ = this.store$.select(additionalFieldsWithNumbers);
  private _types: DynamicField['type'][] = [
    'ADDITIONS_INDEX',
    'ENTITY',
    'FILE',
    'NUMERIC',
    'RULE_SET',
    'SELECTABLE',
    'SHORT_TEXT',
    'SUBSET',
    'SWITCHABLE',
    'GROUP',
  ];

  private _mimeTypes = [
    'image/gif',
    'image/jpeg',
    'image/pjpeg',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/vnd.microsoft.icon',
    'image/vnd.wap.wbmp',
    'image/webp',
  ];

  get fields(): DynamicField[] {
    return this._fields;
  }

  @Input()
  set fields(value: DynamicField[]) {
    this._fields = value;
  }

  @Input()
  set senders(senders: SenderOutput[]) {
    this._senders = senders;
  }

  @Input()
  set goods(goods: GoodOutput[]) {
    this._goods = goods;
  }

  @Input()
  set fieldMap(fieldMap: FieldMap) {
    this._fieldMap = fieldMap;
  }

  @Input()
  set statuses(statuses: StatusOutput[]) {
    this._statuses = statuses;
  }

  @Input()
  set storehouses(value: StorehouseOutput[]) {
    this._storehouses = value;
  }

  get statuses(): StatusOutput[] {
    return this._statuses;
  }

  get storehouses(): StorehouseOutput[] {
    return this._storehouses;
  }

  get senders(): SenderOutput[] {
    return this._senders;
  }

  get goods(): GoodOutput[] {
    return this._goods;
  }

  get fieldMap(): FieldMap {
    return this._fieldMap;
  }

  get form(): UntypedFormGroup {
    return this.controlContainer.control as UntypedFormGroup;
  }

  get possibleValues(): UntypedFormGroup[] {
    return (this.form.controls.values as FormArray<UntypedFormGroup>).controls;
  }

  get schemaForms(): FormGroup<{ type: UntypedFormControl }>[] {
    return (this.form.controls.schema as FormArray<FormGroup<{ type: UntypedFormControl }>>).controls;
  }

  get additionalFields$(): Observable<AdditionalField[]> {
    return this._additionalFields$;
  }

  get types(): DynamicField['type'][] {
    return this._types;
  }

  get mimeTypes(): string[] {
    return this._mimeTypes;
  }

  get invalidName(): boolean {
    const name = this.form.controls.name.value;
    return this.fields.some(field => field.name === name);
  }

  constructor(
    private store$: Store,
    private controlContainer: ControlContainer,
    private fb: UntypedFormBuilder,
  ) {
  }

  addSubset(): void {
    const newSubset = new UntypedFormGroup({
      type: this.fb.control('NUMERIC'),
      name: this.fb.control(null, Validators.required),
      description: this.fb.control(null, Validators.required),
      value: this.fb.control(0),
    });

    const formArray = this.form.controls.schema as UntypedFormArray;
    formArray.push(newSubset);
  }

  addPossibleValue(): void {
    const possibleValues = this.form.controls.values as UntypedFormArray;
    const newPossibleValue = this.fb.group({
      value: this.fb.control(null, Validators.required),
      description: this.fb.control(null, Validators.required),
    });

    possibleValues.push(newPossibleValue);
  }

  removeValue(index: number, controlName: 'schema' | 'values'): void {
    const formArray = this.form.get(controlName) as UntypedFormArray;
    formArray.removeAt(index);
  }

  changeSubsetForm(name: number, type: string | string[]): void {
    const fieldType = this.types.find(candidate => candidate === type);
    if (!fieldType) {
      return;
    }

    const formArray = this.form.controls.schema as UntypedFormArray;
    formArray.setControl(name, getFormGroup(fieldType, this.fb));
  }
}
